/**
 * Auth diagnostic utility.
 *
 * Exposed on `window.deepnetsDiag` by AuthContext.
 * Run `deepnetsDiag()` in the browser console when you see auth weirdness.
 *
 * It captures:
 *  - localStorage token state (presence, decoded payload, expiry)
 *  - React AuthContext state (user, isAuthenticated, isLoading)
 *  - Wallet adapter state (connected, publicKey)
 *  - Server validation of the token (/api/auth/me)
 *  - DOM header state vs React state consistency
 */

import { getApiHost } from './api';

const API_BASE_URL = getApiHost();

export interface AuthSnapshot {
  ts: string;
  localStorage: {
    hasToken: boolean;
    tokenPrefix?: string;
    jwt?: {
      payload: Record<string, unknown>;
      exp: string;
      expUnix: number;
      hoursLeft: number;
      expired: boolean;
    };
    decodeError?: string;
  };
  react: {
    user: unknown;
    isAuthenticated: boolean;
    isLoading: boolean;
  };
  wallet: {
    connected: boolean;
    publicKey: string | null;
  };
  server?: {
    status: number;
    ok: boolean;
    body: unknown;
  };
  dom: {
    headerText: string;
    showsWalletOrName: boolean;
    showsCredits: boolean;
  };
  diagnosis: string;
}

// React state gets injected here by AuthContext
let _reactState: { user: unknown; isAuthenticated: boolean; isLoading: boolean } | null = null;
let _walletState: { connected: boolean; publicKey: string | null } | null = null;

/** Called by AuthContext on every render to keep snapshot current */
export function _setDiagState(
  react: { user: unknown; isAuthenticated: boolean; isLoading: boolean },
  wallet: { connected: boolean; publicKey: string | null },
) {
  _reactState = react;
  _walletState = wallet;
}

/** Decode a JWT payload without verification */
function decodeJwt(token: string) {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Not a valid JWT (expected 3 parts)');
  return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
}

export async function runDiag(): Promise<AuthSnapshot> {
  const now = new Date();
  const token = localStorage.getItem('deepnets_token');

  // --- localStorage ---
  const ls: AuthSnapshot['localStorage'] = { hasToken: !!token };
  if (token) {
    ls.tokenPrefix = token.substring(0, 20) + '…';
    try {
      const payload = decodeJwt(token);
      const exp = new Date(payload.exp * 1000);
      const hoursLeft = (exp.getTime() - now.getTime()) / 3600000;
      ls.jwt = {
        payload,
        exp: exp.toISOString(),
        expUnix: payload.exp,
        hoursLeft: Math.round(hoursLeft * 10) / 10,
        expired: exp < now,
      };
    } catch (e: any) {
      ls.decodeError = e.message;
    }
  }

  // --- React state ---
  const react = _reactState ?? { user: null, isAuthenticated: false, isLoading: false };
  const wallet = _walletState ?? { connected: false, publicKey: null };

  // --- Server validation ---
  let server: AuthSnapshot['server'] | undefined;
  if (token) {
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const body = await r.json().catch(() => null);
      server = { status: r.status, ok: r.ok, body };
    } catch (e: any) {
      server = { status: 0, ok: false, body: { networkError: e.message } };
    }
  }

  // --- DOM ---
  const headerEl = document.querySelector('header');
  const headerText = (headerEl?.textContent || '').trim().replace(/\s+/g, ' ').substring(0, 300);
  const showsWalletOrName = /\.\.\./.test(headerText) || (react.user as any)?.username
    ? headerText.includes((react.user as any)?.username)
    : false;
  const showsCredits = /\d/.test(headerText) && !/deepnets\.ai/.test(headerText.replace(/\d/g, ''));

  const dom = { headerText, showsWalletOrName, showsCredits };

  // --- Diagnosis ---
  let diagnosis: string;
  if (!token) {
    diagnosis = react.isAuthenticated
      ? '🔴 No token in localStorage but React thinks user is authenticated → stale React state'
      : '🟢 No token, not authenticated — consistent';
  } else if (ls.jwt?.expired) {
    diagnosis = react.isAuthenticated
      ? '🔴 Token EXPIRED in localStorage but React still shows authenticated → UI is lying, next API call will 401'
      : '🟡 Token expired and React cleared state — but token still in localStorage (should be removed)';
  } else if (server && !server.ok) {
    diagnosis = react.isAuthenticated
      ? `🔴 Server REJECTS token (${server.status}) but React shows authenticated → stale state, user sees UI but API calls fail`
      : `🟡 Server rejects token (${server.status}) and React is not authenticated — token should be cleared from localStorage`;
  } else if (server?.ok && react.isAuthenticated) {
    diagnosis = '🟢 All consistent — token valid, server accepts it, React shows authenticated';
  } else if (server?.ok && !react.isAuthenticated) {
    diagnosis = '🟡 Server accepts token but React shows NOT authenticated → refreshUser() may not have run or failed';
  } else {
    diagnosis = '🟡 Unable to fully determine — check individual fields';
  }

  const snapshot: AuthSnapshot = {
    ts: now.toISOString(),
    localStorage: ls,
    react: { user: react.user, isAuthenticated: react.isAuthenticated, isLoading: react.isLoading },
    wallet,
    server,
    dom,
    diagnosis,
  };

  // Pretty-print to console
  console.group('🔍 deepnetsDiag()');
  console.log('%c' + diagnosis, 'font-size: 14px; font-weight: bold;');
  console.log('');
  console.log('%clocalStorage', 'font-weight: bold; color: #5b6ef5;');
  console.table({
    'Has token': ls.hasToken,
    'Token prefix': ls.tokenPrefix ?? '—',
    'JWT expired': ls.jwt?.expired ?? '—',
    'Hours left': ls.jwt?.hoursLeft ?? '—',
    'Expires': ls.jwt?.exp ?? '—',
  });
  if (ls.jwt?.payload) {
    console.log('JWT payload:', ls.jwt.payload);
  }
  console.log('');
  console.log('%cReact AuthContext', 'font-weight: bold; color: #5b6ef5;');
  console.table({
    'isAuthenticated': react.isAuthenticated,
    'isLoading': react.isLoading,
    'user.id': (react.user as any)?.id ?? '—',
    'user.username': (react.user as any)?.username ?? '—',
    'user.walletAddress': (react.user as any)?.walletAddress ?? '—',
    'user.credits': (react.user as any)?.credits ?? '—',
  });
  console.log('');
  console.log('%cWallet Adapter', 'font-weight: bold; color: #5b6ef5;');
  console.table({
    'connected': wallet.connected,
    'publicKey': wallet.publicKey ?? '—',
  });
  if (server) {
    console.log('');
    console.log('%cServer /api/auth/me', 'font-weight: bold; color: #5b6ef5;');
    console.log(`Status: ${server.status} ${server.ok ? '✅' : '❌'}`);
    console.log('Response:', server.body);
  }
  console.groupEnd();

  return snapshot;
}
